import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Crown, ChevronRight, ChevronDown, ArrowRight, Route, Users, Zap, Trophy } from "lucide-react";
import { api } from "../lib/api";
import { Flag } from "../components/Flag";
import { Loader, Empty } from "../components/UI";
import { ClubCrest } from "../components/ClubCrest";
import { StatusPill } from "./CupPage";
import { TIER } from "./ContinentalPage";
import { useEventStream } from "../lib/sse";

const CONTAINER = "mx-auto max-w-[1600px] px-4 sm:px-8 lg:px-12";
const TIERS = [1, 2, 3];

// Qualification origin → human label (real backend values only; both CUP_WINNER and
// DOMESTIC_CUP_WINNER exist in the data and are shown verbatim-humanised).
const humanize = (s) => (s || "").replace(/_/g, " ");

function EuroTieCard({ t }) {
  const isFinal = t.round === "FINAL";
  const homeWin = t.winner_club && t.winner_club.id === t.home_club?.id;
  const awayWin = t.winner_club && t.winner_club.id === t.away_club?.id;
  const seed = (s) => (s && s <= 8 ? <span className="text-slate-600 text-[9px] font-mono ml-1">#{s}</span> : null);
  return (
    <Link to={`/cup/tie/${t.id}`} data-testid={`continental-tie-${t.id}`}
      className={`block rounded-xl bg-[#0f0f0f] border p-4 hover:border-[#E5B842]/50 ${isFinal ? "foil-border" : "border-[#2b2512]"}`}
      style={{ transition: "border-color 0.15s ease, transform 0.15s ease" }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = ""; }}>
      <div className="flex items-center justify-between mb-3">
        <span className="font-mono text-[9px] uppercase tracking-wider text-slate-500">{isFinal ? "Final · Single match" : "Two legs · Agg Team Pts"}</span>
        <StatusPill status={t.status} />
      </div>
      <div className="space-y-2">
        <Row club={t.home_club} seed={seed(t.home_seed)} pts={t.aggregate_home} win={homeWin} />
        <Row club={t.away_club} seed={seed(t.away_seed)} pts={t.aggregate_away} win={awayWin} />
      </div>
      <div className="mt-3 pt-3 border-t border-[#2b2512] flex items-center justify-between">
        {t.extra_duels_required
          ? <span className="inline-flex items-center gap-1 text-[10px] font-mono font-bold uppercase tracking-wider text-fuchsia-400"><Zap className="w-3 h-3" /> Extra Duel</span>
          : <span className="text-[10px] font-mono uppercase tracking-wider text-slate-600">Agg decided</span>}
        {t.winner_club
          ? <span className="inline-flex items-center gap-1 text-[10px] font-mono font-bold uppercase tracking-wider text-emerald-300"><Crown className="w-3 h-3 text-[#E5B842]" />{t.winner_club.short_name || t.winner_club.name} {isFinal ? "wins" : "advances"}</span>
          : <span className="inline-flex items-center gap-1 text-[10px] font-mono text-slate-500">View tie <ChevronRight className="w-3 h-3" /></span>}
      </div>
    </Link>
  );
}

function Row({ club, seed, pts, win }) {
  return (
    <div className="flex items-center gap-2">
      <ClubCrest club={club} size={26} />
      <span className={`flex-1 truncate font-heading font-bold uppercase text-sm ${win ? "text-[#E5B842]" : "text-slate-200"}`}>{club?.short_name || club?.name}{seed}</span>
      <span className={`font-heading text-xl font-black tabular-nums ${win ? "text-[#E5B842]" : "text-slate-100"}`}>{pts}</span>
    </div>
  );
}

export default function ContinentalTierPage() {
  const { ident } = useParams();
  const [data, setData] = useState(null);
  const [season, setSeason] = useState(null);
  const [missing, setMissing] = useState(false);

  const load = useCallback(() => {
    api.get(`/continental/${ident}`).then(({ data }) => {
      setData(data);
      if (data?.competition?.id) api.get(`/competitions/${data.competition.id}`).then(({ data: c }) => setSeason(c?.season?.name)).catch(() => {});
    }).catch(() => setMissing(true));
  }, [ident]);
  useEffect(() => { setData(null); setMissing(false); load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);
  useEventStream("/live/stream", (ev) => { if (ev?.type?.startsWith?.("continental.")) load(); });

  if (missing) return <div className={`${CONTAINER} py-16 animate-fade-up`}><Empty label="Competition not found." /></div>;
  if (!data) return <Loader label="Loading competition…" />;

  const { competition: c, teams, rounds, champion, champion_path } = data;
  const id = TIER[c.tier] || TIER[2];
  const Icon = id.Icon;

  return (
    <div className="animate-fade-up pb-20">
      {/* HEADER */}
      <section className="relative overflow-hidden border-b border-[rgba(212,175,55,0.22)] bg-gradient-to-b from-[#0f0f0f] to-[#000000]">
        <div className="absolute inset-0 mesh-grid opacity-[0.14] pointer-events-none" />
        <div className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(90% 130% at 85% 0%, ${id.glow} 0%, transparent 55%)` }} />
        <div className={`${CONTAINER} relative py-9 sm:py-11`}>
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-4" data-testid="europe-breadcrumb">
            <Link to="/continental" className="hover:text-[#E5B842]" style={{ transition: "color 0.15s ease" }}>Europe</Link>
            <ChevronRight className="w-3 h-3 text-slate-700" />
            <span style={{ color: id.accent }}>{id.label}</span>
            <ChevronRight className="w-3 h-3 text-slate-700" />
            <span className="text-slate-300">{id.short}</span>
          </nav>
          <div className="flex flex-col lg:flex-row lg:items-center gap-5">
            <div className="shrink-0 w-16 h-16 rounded-2xl bg-[#000000] flex items-center justify-center border" style={{ borderColor: id.border }}>
              <Icon className="w-8 h-8" style={{ color: id.accent }} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap text-[11px] font-mono uppercase tracking-[0.2em]">
                <span className="font-bold" style={{ color: id.accent }}>ESBF · {id.label}</span>
                <StatusPill status={c.status} />
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight text-slate-50 leading-none mt-1.5 wsbf-slant-block break-words">{c.name}</h1>
              <div className="mt-2 flex items-center gap-x-5 gap-y-1 flex-wrap font-mono text-[12px] text-slate-400">
                {season && <span>Season {season}</span>}
                <span className="inline-flex items-center gap-1.5"><Users className="w-3.5 h-3.5" style={{ color: id.accent }} />{teams.length} clubs</span>
              </div>
            </div>
          </div>
          {/* Tier switcher (Europe-level) */}
          <div className="mt-6 inline-flex flex-wrap gap-1 p-1 rounded-xl bg-[#0a0a0a] border border-[#2b2512]" data-testid="europe-tier-switcher">
            {TIERS.map((n) => {
              const tid = TIER[n];
              const active = Number(c.tier) === n;
              return (
                <Link key={n} to={`/continental/${n}`} data-testid={`tier-switch-${n}`}
                  className="px-4 py-2 rounded-lg text-xs sm:text-sm font-heading font-bold uppercase tracking-wide whitespace-nowrap"
                  style={{ transition: "background-color 0.15s ease, color 0.15s ease", backgroundColor: active ? tid.accent : "transparent", color: active ? "#000000" : "#cbd5e1" }}>
                  {tid.label} · {tid.short}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <div className={`${CONTAINER} pt-8`}>
        {/* CHAMPION HERO */}
        {champion && (
          <Link to={`/club/${champion.id}`} data-testid="continental-champion"
            className="group relative overflow-hidden flex items-center gap-5 rounded-2xl foil-border bg-gradient-to-r from-[#1a1407] to-[#070707] p-6 mb-6"
            style={{ borderColor: id.border, transition: "border-color 0.15s ease" }}>
            <div className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(70% 160% at 10% 0%, ${id.glow} 0%, transparent 60%)` }} />
            <Crown className="relative w-9 h-9 shrink-0" style={{ color: id.accent }} />
            <ClubCrest club={champion} size={60} />
            <div className="relative min-w-0">
              <div className="text-[11px] uppercase tracking-[0.28em] font-mono font-bold" style={{ color: id.accent }}>European Champions</div>
              <div className="font-heading text-2xl sm:text-3xl font-black uppercase tracking-tight text-slate-50 leading-none mt-1 break-words">{champion.name}</div>
              <div className="text-[11px] font-mono text-slate-500 mt-1">{id.label} Champions</div>
            </div>
            <ChevronRight className="relative ml-auto w-6 h-6 text-slate-600 group-hover:text-[#E5B842] shrink-0" style={{ transition: "color 0.15s ease" }} />
          </Link>
        )}

        {/* CHAMPION'S EUROPEAN PATH */}
        {champion && champion_path?.length > 0 && (
          <section className="mb-10" data-testid="champion-path">
            <div className="flex items-center gap-2 mb-4">
              <Route className="w-4 h-4" style={{ color: id.accent }} />
              <h2 className="font-heading text-lg font-bold uppercase tracking-tight text-slate-50">Champion's European Path</h2>
            </div>
            <div className="flex flex-col lg:flex-row lg:items-stretch gap-3">
              {champion_path.map((p, i) => {
                const finalLeg = p.round === "FINAL";
                return (
                  <div key={p.round} className="contents lg:flex lg:items-center">
                    <div className={`flex-1 rounded-xl bg-[#0f0f0f] border p-4 ${finalLeg ? "foil-border" : "border-[#2b2512]"}`} style={finalLeg ? { borderColor: id.border } : {}}>
                      <div className="font-mono text-[10px] uppercase tracking-wider mb-2" style={{ color: id.accent }}>{p.round_name}</div>
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-[10px] font-mono text-slate-500 uppercase">vs</span>
                        <ClubCrest club={p.opponent} size={24} />
                        <Link to={`/club/${p.opponent?.id}`} className="text-sm text-slate-200 truncate hover:text-[#E5B842]" style={{ transition: "color 0.15s ease" }}>{p.opponent?.short_name || p.opponent?.name}</Link>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="font-heading text-2xl font-black text-[#E5B842] tabular-nums">{p.agg_for}</span>
                        <span className="text-slate-600">–</span>
                        <span className="font-heading text-2xl font-black text-slate-200 tabular-nums">{p.agg_against}</span>
                        <span className="text-[9px] font-mono uppercase text-slate-500 ml-1 leading-tight">{finalLeg ? "Team Pts" : "Agg Team Pts"}</span>
                      </div>
                    </div>
                    {i < champion_path.length - 1 && (
                      <>
                        <div className="hidden lg:flex items-center px-1 text-slate-700"><ArrowRight className="w-5 h-5" /></div>
                        <div className="flex lg:hidden justify-center py-1 text-slate-700"><ChevronDown className="w-5 h-5" /></div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ROAD TO THE FINAL */}
        <div className="flex items-center gap-3 mb-5">
          <Trophy className="w-5 h-5" style={{ color: id.accent }} />
          <h2 className="font-heading text-2xl font-extrabold uppercase tracking-tight text-slate-50">Road to the Final</h2>
        </div>
        {rounds.length === 0 ? <Empty label="No rounds published yet." /> : (
          <div className="lg:overflow-x-auto no-scrollbar lg:pb-4 mb-12" data-testid="continental-road">
            <div className="flex flex-col lg:flex-row lg:items-stretch gap-4 lg:gap-3 lg:min-w-max">
              {rounds.map((r, i) => (
                <div key={r.code} className="contents lg:flex lg:items-center">
                  <section data-testid={`continental-round-${r.code}`} className="lg:w-[340px] lg:shrink-0 lg:self-center">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className={`font-heading text-sm font-black uppercase tracking-wider ${r.code === "FINAL" ? "" : "text-slate-200"}`} style={r.code === "FINAL" ? { color: id.accent } : {}}>{r.name}</h3>
                      <span className="font-mono text-[10px] text-slate-600">{r.ties.length} tie{r.ties.length === 1 ? "" : "s"}</span>
                    </div>
                    <div className="space-y-3">{r.ties.map((t) => <EuroTieCard key={t.id} t={t} />)}</div>
                  </section>
                  {i < rounds.length - 1 && (
                    <>
                      <div className="hidden lg:flex items-center px-1 text-slate-700"><ArrowRight className="w-5 h-5" /></div>
                      <div className="flex lg:hidden justify-center py-1 text-slate-700"><ChevronDown className="w-5 h-5" /></div>
                    </>
                  )}
                </div>
              ))}
              {champion && (
                <>
                  <div className="hidden lg:flex items-center px-1 text-slate-700"><ArrowRight className="w-5 h-5" /></div>
                  <div className="flex lg:hidden justify-center py-1 text-slate-700"><ChevronDown className="w-5 h-5" /></div>
                  <div className="lg:w-[260px] lg:shrink-0 lg:self-center">
                    <div className="text-[10px] font-mono uppercase tracking-[0.2em] mb-3 text-center lg:text-left" style={{ color: id.accent }}>Champion</div>
                    <Link to={`/club/${champion.id}`} className="flex flex-col items-center gap-3 rounded-xl foil-border bg-gradient-to-b from-[#1a1407] to-[#070707] p-6" style={{ borderColor: id.border }}>
                      <Crown className="w-7 h-7" style={{ color: id.accent }} />
                      <ClubCrest club={champion} size={52} />
                      <span className="font-heading font-black uppercase text-center text-slate-50 leading-snug">{champion.name}</span>
                    </Link>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* QUALIFIED CLUBS */}
        <section data-testid="continental-teams">
          <div className="flex items-center gap-2 mb-1">
            <Users className="w-5 h-5" style={{ color: id.accent }} />
            <h2 className="font-heading text-2xl font-extrabold uppercase tracking-tight text-slate-50">Qualified Clubs</h2>
          </div>
          <p className="text-[11px] font-mono text-slate-500 mb-5">How each club reached {id.short} — by qualification origin.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {teams.map((t) => (
              <Link key={t.club_id} to={`/club/${t.club_id}`} data-testid={`continental-team-${t.seed}`}
                className="flex items-center gap-3 rounded-xl bg-[#0f0f0f] border border-[#2b2512] p-4 hover:border-[#E5B842]/45" style={{ transition: "border-color 0.15s ease" }}>
                <span className="font-heading text-lg font-black text-slate-600 w-6 tabular-nums shrink-0">{t.seed}</span>
                <ClubCrest club={t.club} size={40} />
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-bold uppercase text-slate-100 text-sm leading-snug"><span className="inline-flex items-center gap-1.5">{t.country && <Flag country={t.country} />}{t.club?.name}</span></div>
                  <div className="font-mono text-[10px] uppercase tracking-wider mt-0.5" style={{ color: id.accent }}>{humanize(t.qualification_method)}</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
